/**
 * Canonical evaluation camera setups (mirrors Backend evaluationCameraSetup.js).
 * C/D are player table slots only; camera A covers C, camera B covers D.
 */

export const CAMERA_SETUPS = Object.freeze({
  SPECTATOR_A: "spectator_a",
  SPECTATOR_B: "spectator_b",
  REFEREE_A: "referee_a",
  REFEREE_B: "referee_b",
});

export const DEFAULT_CAMERA_SETUP = CAMERA_SETUPS.SPECTATOR_A;

export const SETUP_LABELS = Object.freeze({
  spectator_a: "Spectator A",
  spectator_b: "Spectator B",
  referee_a: "Referee A",
  referee_b: "Referee B",
});

const FILENAME_SETUP_RE = /_(?:spec|spectator)([AB])_/i;
const FILENAME_REF_RE = /_(?:ref|referee)([AB])_/i;
const LEGACY_SIDE_RE = /SmartTT_T\d+_([ABCD])_/i;
const LEGACY_SIDE_SEQ_RE = /_([ABCD])_seq\d+/i;

export function normalizeCameraSetup(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (
    s === "spectator_a" ||
    s === "spec_a" ||
    s === "speca" ||
    s === "spectatora"
  ) {
    return CAMERA_SETUPS.SPECTATOR_A;
  }
  if (
    s === "spectator_b" ||
    s === "spec_b" ||
    s === "specb" ||
    s === "spectatorb"
  ) {
    return CAMERA_SETUPS.SPECTATOR_B;
  }
  if (s === "referee_a" || s === "ref_a" || s === "refa" || s === "refereea") {
    return CAMERA_SETUPS.REFEREE_A;
  }
  if (s === "referee_b" || s === "ref_b" || s === "refb" || s === "refereeb") {
    return CAMERA_SETUPS.REFEREE_B;
  }
  const letter = s.toUpperCase();
  if (letter === "A" || letter === "C") return CAMERA_SETUPS.SPECTATOR_A;
  if (letter === "B" || letter === "D") return CAMERA_SETUPS.SPECTATOR_B;
  return null;
}

export function cameraSetupFromLegacyPosition(position) {
  const p = String(position || "")
    .trim()
    .toUpperCase();
  if (p === "A" || p === "C") return CAMERA_SETUPS.SPECTATOR_A;
  if (p === "B" || p === "D") return CAMERA_SETUPS.SPECTATOR_B;
  return null;
}

export function orientedPlayerFromSetup(setup) {
  const id = normalizeCameraSetup(setup) || DEFAULT_CAMERA_SETUP;
  return id.endsWith("_b") ? "B" : "A";
}

export function geometryModeFromSetup(setup) {
  const id = normalizeCameraSetup(setup) || DEFAULT_CAMERA_SETUP;
  return id.startsWith("referee") ? "referee" : "spectator";
}

export function filenameTokenForSetup(setup) {
  const id = normalizeCameraSetup(setup) || DEFAULT_CAMERA_SETUP;
  switch (id) {
    case CAMERA_SETUPS.SPECTATOR_B:
      return "specB";
    case CAMERA_SETUPS.REFEREE_A:
      return "refA";
    case CAMERA_SETUPS.REFEREE_B:
      return "refB";
    default:
      return "specA";
  }
}

export function parseCameraSetupFromFilename(name) {
  if (!name) return null;
  const raw = String(name);
  const spec = raw.match(FILENAME_SETUP_RE);
  if (spec) return normalizeCameraSetup(`spectator_${spec[1]}`);
  const ref = raw.match(FILENAME_REF_RE);
  if (ref) return normalizeCameraSetup(`referee_${ref[1]}`);
  const legacy = raw.match(LEGACY_SIDE_RE) || raw.match(LEGACY_SIDE_SEQ_RE);
  if (legacy) return cameraSetupFromLegacyPosition(legacy[1]);
  return null;
}

function parseViewSetupObject(viewSetup) {
  if (viewSetup == null || viewSetup === "") return null;
  if (typeof viewSetup === "object" && !Array.isArray(viewSetup)) {
    return viewSetup;
  }
  try {
    const parsed = JSON.parse(String(viewSetup));
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : null;
  } catch {
    return null;
  }
}

export function cameraSetupFromViewSetup(viewSetup) {
  const vs = parseViewSetupObject(viewSetup);
  if (!vs) return null;
  const fromSetup = normalizeCameraSetup(
    vs.camera_setup || vs.cameraSetup || vs.camera_setup_id
  );
  if (fromSetup) return fromSetup;
  if (vs.user && typeof vs.user === "object") {
    const nested = normalizeCameraSetup(
      vs.user.camera_setup || vs.user.cameraSetup || vs.user.camera_setup_id
    );
    if (nested) return nested;
  }
  const legacy =
    vs.camera_position ||
    vs.cameraPosition ||
    vs.user?.camera_position ||
    vs.user?.cameraPosition ||
    (Array.isArray(vs.positions) ? vs.positions[0] : null);
  return cameraSetupFromLegacyPosition(legacy);
}

export function cameraOrdinalFromViewSetup(viewSetup) {
  const vs = parseViewSetupObject(viewSetup);
  if (!vs) return null;
  const n = Number(vs.camera_ordinal ?? vs.cameraOrdinal);
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : null;
}

export function resolveCameraSetup({
  bodySetup = null,
  viewSetup = null,
  filename = null,
  cameraPosition = null,
} = {}) {
  return (
    normalizeCameraSetup(bodySetup) ||
    cameraSetupFromViewSetup(viewSetup) ||
    parseCameraSetupFromFilename(filename) ||
    cameraSetupFromLegacyPosition(cameraPosition) ||
    DEFAULT_CAMERA_SETUP
  );
}

export function normalizeCameraOrdinal(raw) {
  const n = Number(raw);
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : null;
}

export function clipCameraFromSources({
  viewSetup = null,
  filename = null,
  cameraPosition = null,
} = {}) {
  const cameraSetup = resolveCameraSetup({
    viewSetup,
    filename,
    cameraPosition,
  });
  const cameraOrdinal = cameraOrdinalFromViewSetup(viewSetup) || 1;
  return {
    cameraSetup,
    cameraOrdinal,
    geometryMode: geometryModeFromSetup(cameraSetup),
  };
}

export function attachCameraSetupToViewSetup(viewSetup, cameraMeta) {
  const base =
    viewSetup && typeof viewSetup === "object" && !Array.isArray(viewSetup)
      ? { ...viewSetup }
      : {};
  if (cameraMeta?.cameraSetup) base.camera_setup = cameraMeta.cameraSetup;
  if (cameraMeta?.cameraOrdinal != null) {
    base.camera_ordinal = cameraMeta.cameraOrdinal;
  }
  return base;
}

export function formatCameraSetupLabel(setup, ordinal = null, t = null) {
  const id = normalizeCameraSetup(setup) || DEFAULT_CAMERA_SETUP;
  const fallback = SETUP_LABELS[id] || SETUP_LABELS[DEFAULT_CAMERA_SETUP];
  const base = t
    ? t(`camera_setup_${id}`, fallback) || fallback
    : fallback;
  const ord = normalizeCameraOrdinal(ordinal);
  if (ord != null && ord > 1) {
    return t
      ? t("camera_setup_with_ordinal", "{{label}} #{{n}}", {
          label: base,
          n: ord,
          defaultValue: `${base} #${ord}`,
        }) || `${base} #${ord}`
      : `${base} #${ord}`;
  }
  return base;
}

export function isSpectatorGeometrySetup(setup) {
  return geometryModeFromSetup(setup) === "spectator";
}

export function normalizeCameraView(raw) {
  const s = String(raw || "")
    .trim()
    .toLowerCase();
  if (!s) return null;
  if (s.startsWith("ref")) return "referee";
  if (s.startsWith("spec")) return "spectator";
  return null;
}

export function buildCameraSetupId(viewType, orientedPlayer) {
  const mode = normalizeCameraView(viewType) || "spectator";
  const letter = String(orientedPlayer || "A")
    .trim()
    .toUpperCase();
  const side = letter === "B" || letter === "D" ? "b" : "a";
  return normalizeCameraSetup(`${mode}_${side}`) || DEFAULT_CAMERA_SETUP;
}
